import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';

import {
  CheckCircle,
  CircleNotch,
  Clock,
  FileText,
  Funnel,
  WarningCircle
} from '@phosphor-icons/react';
/**
 * Sessions List Page - Phase 11: Employee Sessions
 */
const SessionsList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { activeOrganization } = useOrganization();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [agentFilter] = useState(searchParams.get('agent_id') || '');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (activeOrganization) {
      fetchSessions();
    }
  }, [activeOrganization, statusFilter, page, agentFilter]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '20');
      if (statusFilter) params.set('status', statusFilter);
      if (agentFilter) params.set('agent_id', agentFilter);

      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/my/sessions?${params.toString()}`,
        {},
        activeOrganization.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Error fetching sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="flex items-center gap-1 text-xs px-2 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded">
            <CheckCircle size={12} />
            Completed
          </span>
        );
      case 'running':
        return (
          <span className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded">
            <CircleNotch size={12} className="animate-spin" />
            Running
          </span>
        );
      case 'failed':
        return (
          <span className="flex items-center gap-1 text-xs px-2 py-1 bg-red-500/20 text-red-400 border border-red-500/30 rounded">
            <WarningCircle size={12} />
            Failed
          </span>
        );
      case 'queued':
        return (
          <span className="flex items-center gap-1 text-xs px-2 py-1 bg-gray-500/20 text-gray-400 border border-gray-500/30 rounded">
            <Clock size={12} />
            Queued
          </span>
        );
      default:
        return null;
    }
  };

  const formatTime = (timestamp) => {
    // Ensure timestamp is treated as UTC if no timezone specified
    // MongoDB stores UTC but without 'Z' suffix, so we need to handle both cases
    let dateStr = timestamp;
    if (typeof timestamp === 'string' && !timestamp.endsWith('Z') && !timestamp.includes('+')) {
      dateStr = timestamp + 'Z';
    }
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 0) return 'Just now'; // Handle future dates gracefully
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading && sessions.length === 0) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <div className="h-8 bg-gray-700 rounded w-48 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-700 rounded w-64 animate-pulse"></div>
          </div>

          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="glass rounded-lg p-6 animate-pulse">
                <div className="h-6 bg-gray-700 rounded w-1/3 mb-2"></div>
                <div className="h-4 bg-gray-700 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">My Sessions</h1>
            <p className="text-gray-400">{total} total sessions</p>
          </div>

          {/* Funnel */}
          <div className="flex items-center gap-2">
            <Funnel size={16} className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 glass border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
            >
              <option value="">All Status</option>
              <option value="completed">Completed</option>
              <option value="running">Running</option>
              <option value="failed">Failed</option>
              <option value="queued">Queued</option>
            </select>
          </div>
        </div>

        {/* Sessions List */}
        {sessions.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center border border-[rgba(255,255,255,0.1)]">
            <FileText size={64} className="text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Sessions Yet</h3>
            <p className="text-gray-400 mb-6">
              Start running agents to see your session history here.
            </p>
            <button
              onClick={() => navigate('/partner-pro/workspace/my-agents')}
              className="px-6 py-3 bg-[#6366F1] hover:bg-[#5558E6] text-white rounded-lg transition-colors"
            >
              Go to My Agents
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {sessions.map((session) => (
              <div
                key={session.session_id}
                onClick={() => navigate(`/partner-pro/workspace/sessions/${session.session_id}`)}
                className="glass rounded-lg p-6 border border-[rgba(255,255,255,0.1)] hover:border-[#6366F1] transition-all cursor-pointer group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white group-hover:text-[#6366F1] transition-colors">
                        {session.agent_name}
                      </h3>
                      {getStatusBadge(session.status)}
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <FileText size={14} />
                        {session.input_type}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {formatTime(session.created_at)}
                      </span>
                      {session.source && (
                        <span className="px-2 py-0.5 bg-[rgba(255,255,255,0.05)] rounded text-xs">
                          {session.source}
                        </span>
                      )}
                    </div>
                  </div>

                  <button className="text-[#6366F1] hover:text-[#8B5CF6] text-sm font-medium">
                    View →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > 20 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 glass rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgba(255,255,255,0.1)]"
            >
              Previous
            </button>
            <span className="text-gray-400">Page {page}</span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page * 20 >= total}
              className="px-4 py-2 glass rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgba(255,255,255,0.1)]"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionsList;